import { Injectable, Injector } from "@angular/core";
import { Store } from "@ngxs/store";
import { CacheRespuestas } from "../CacheRespuestas";
import { Respuesta } from "../../../../../../layout/investigaciones/ver-respuestas/Respuesta";
import { CloudData } from "angular-tag-cloud-module";
import { CuotaDTO, PreguntaDTO, RespuestaDiaDTO, RespuestaUsuario } from "@DTOS";
import { CloudDataConCantidad, ProcesoDatos } from "@shared/clases";
import { PreguntasTipoEnum, TipoCacheRespuestaEnum } from "@shared/enums";
import { InvestigacionesAcciones, State } from "@shared/states";
import { filter, map, take } from "rxjs/operators";
import { firstValueFrom } from "rxjs";

@Injectable()
export abstract class ObtenerRespuestas {
    protected constructor(
        protected store: Store, protected cacheRespuestas: CacheRespuestas, protected injector: Injector
    ) {}

    protected async obtenerRespuestasPregunta(
        pregunta: PreguntaDTO, respuestas: RespuestaUsuario[]
    ): Promise<Respuesta[] | CloudData[]> {
        this.cargarProcesoDatos(respuestas);
        if (pregunta.texto === "Cuotas" || pregunta.texto === "Rangos de edad" || pregunta.texto === "Géneros" ||
            pregunta.texto === "Países" || pregunta.texto === "Funnel" || pregunta.texto === "Por día") {
            return await this.obtenerRespuestasPorEje(pregunta.texto);
        }
        return this.obtenerRespuestasPreguntaPorTipo(pregunta);
    }

    protected obtenerRespuestasProcesoDatos(pregunta: PreguntaDTO, respuestasUsuario: RespuestaUsuario[]): Respuesta[] {
        const procesoDatos = this.injector.get(ProcesoDatos);
        procesoDatos.cargarDatos();
        procesoDatos.procesarDesde(respuestasUsuario);
        if (pregunta.texto === "Rangos de edad" || pregunta.texto === "Géneros" || pregunta.texto === "Países") {
            return ObtenerRespuestas.obtenerRespuestasEjeProcesoDatos(pregunta, procesoDatos);
        }
        return this.obtenerRespuestasPreguntaProcesoDatos(pregunta, procesoDatos);
    }

    private static obtenerRespuestasEjeProcesoDatos(pregunta: PreguntaDTO, procesoDatos: ProcesoDatos): Respuesta[] {
        switch (pregunta.texto) {
        case "Rangos de edad":
        case "edad":
            return procesoDatos.rangosEdad;
        case "Géneros":
        case "genero":
            return procesoDatos.generos;
        case "Países":
        case "pais":
            return procesoDatos.paises;
        default:
            return [];
        }
    }

    private obtenerRespuestasPreguntaProcesoDatos(pregunta: PreguntaDTO, procesoDatos: ProcesoDatos): Respuesta[] {
        const respuestas: Respuesta[] = [];
        let cantidadRespuestas = 0;
        for (const item of pregunta.items) {
            if (!this.cacheRespuestas.existenRespuestaProcesada(item.id)) {
                continue;
            }
            const respuestaExtra = procesoDatos.respuestasProcesadas.get(item.id);
            if (respuestaExtra == null) {
                continue;
            }
            cantidadRespuestas += respuestaExtra.usuarios.size;
            respuestas.push({
                name: item.texto,
                value: respuestaExtra.cantidad,
                extra: respuestaExtra
            });
        }
        return respuestas.map(respuesta => ({
            ...respuesta,
            value: (respuesta.value ?? 0) / cantidadRespuestas
        }));
    }

    private cargarProcesoDatos(respuestas: RespuestaUsuario[]) {
        const procesoDatos = this.injector.get(ProcesoDatos);
        procesoDatos.cargarDatos();
        procesoDatos.procesarDesde(respuestas);
        this.cacheRespuestas.guardarDesdeProcesoDatos(procesoDatos);
    }

    private obtenerRespuestasPreguntaPorTipo(
        pregunta: PreguntaDTO
    ): Respuesta[] | CloudData[] {
        if ([PreguntasTipoEnum.MATRIX_SINGLE, PreguntasTipoEnum.MATRIX_MULTIPLE].includes(pregunta.tipo)) {
            return this.obtenerRespuestasMatriz(pregunta);
        }

        if (pregunta.tipo === PreguntasTipoEnum.TEXT) {
            return this.obtenerRespuestasTexto(pregunta);
        }

        if (pregunta.tipo === PreguntasTipoEnum.RANKING) {
            return this.obtenerRespuestasRanking(pregunta);
        }

        return this.obtenerRespuestasTipoSimple(pregunta);
    }

    private obtenerRespuestasMatriz(preguntaMatriz: PreguntaDTO): Respuesta[] {
        const respuestas: Respuesta[] = [];
        preguntaMatriz.preguntas?.forEach(pregunta => {
            respuestas.push({
                name: pregunta.texto,
                series: this.obtenerRespuestasTipoSimple(pregunta)
            });
        });
        return respuestas;
    }

    private obtenerRespuestasRanking(preguntaRanking: PreguntaDTO): Respuesta[] {
        const respuestasExtra = new Map<number, Respuesta[]>();
        preguntaRanking.items.forEach(item => {
            const respuestaExtra = this.cacheRespuestas.obtenerRespuestaProcesada(item.id);
            Array.from(respuestaExtra.valoresRango?.keys() ?? []).forEach(orden => {
                if (!respuestasExtra.has(orden)) {
                    respuestasExtra.set(orden, []);
                }
                respuestasExtra.get(orden)!.push({
                    name: item.texto,
                    value: respuestaExtra.cantidad,
                    extra: respuestaExtra
                });
            });
        });
        const respuestas: Respuesta[] = [];
        Array.from(respuestasExtra.keys())
            .forEach(pregunta => {
                respuestas.push({
                    name: pregunta.toString(),
                    series: respuestasExtra.get(pregunta)?.sort((a, b) => a.name.localeCompare(b.name))
                });
            });
        return respuestas;
    }

    private obtenerRespuestasTexto(pregunta: PreguntaDTO): CloudData[] {
        if (pregunta.items.length === 0) {
            // TODO: Pregunta texto no tiene items
            return [];
        }
        const item = pregunta.items[0];
        if (!this.cacheRespuestas.existenRespuestaProcesada(item.id)) {
            // TODO: Pregunta no tiene items procesados
            return [];
        }
        const respuestaExtra = this.cacheRespuestas.obtenerRespuestaProcesada(item.id);
        const mapPalabras = new Map<string, number>();

        respuestaExtra.valores?.forEach(texto => {
            if (mapPalabras.has(texto)) {
                const cantidadGuardada = mapPalabras.get(texto) ?? 0;

                mapPalabras.set(texto, cantidadGuardada + 1);
            } else {
                mapPalabras.set(texto, 1);
            }
        });

        const entradas = Array.from(mapPalabras.entries());
        const respuestasWordCloud: CloudDataConCantidad[] = [];

        entradas.forEach(entrada => {
            respuestasWordCloud.push({
                text: entrada[0],
                weight: entrada[1],
                extra: {
                    usuarios: respuestaExtra.usuarios ?? new Set<number>()
                }
            });
        });

        return respuestasWordCloud.sort((a, b): number => (b.weight ?? 0) - (a.weight ?? 0)).splice(0, 50);
    }

    private obtenerRespuestasTipoSimple(pregunta: PreguntaDTO): Respuesta[] {
        const respuestas: Respuesta[] = [];
        let cantidadRespuestas = 0;
        for (const item of pregunta.items) {
            if (!this.cacheRespuestas.existenRespuestaProcesada(item.id)) {
                continue;
            }
            const respuestaExtra = this.cacheRespuestas.obtenerRespuestaProcesada(item.id);
            cantidadRespuestas += respuestaExtra.usuarios.size;
            respuestas.push({
                name: item.texto,
                value: respuestaExtra.cantidad,
                extra: respuestaExtra
            });
        }
        return respuestas.map(respuesta => ({
            ...respuesta,
            value: (respuesta.value ?? 0) / cantidadRespuestas
        }));
    }

    private async obtenerRespuestasPorEje(eje: string): Promise<Respuesta[]> {
        let respuestas: Respuesta[];
        switch (eje) {
        case "Cuotas":
        case "cuotas":
            return await this.cargarCuotas();
        case "Funnel":
            return await this.cargarFunnel();
        case "Rangos de edad":
        case "edad": {
            respuestas = this.cacheRespuestas.obtenerRespuestas(TipoCacheRespuestaEnum.RANGOS_EDAD);
            break;
        }
        case "Géneros":
        case "genero": {
            respuestas = this.cacheRespuestas.obtenerRespuestas(TipoCacheRespuestaEnum.GENEROS);
            break;
        }
        case "Países":
        case "pais":
            respuestas = this.cacheRespuestas.obtenerRespuestas(TipoCacheRespuestaEnum.PAISES);
            break;
        case "Por día":
        default: {
            return firstValueFrom(
                this.store.select((state: State) =>
                    state.investigaciones.respuestasDiaInvestigacionSeleccionada)
                    .pipe(
                        filter(respuestasDia => respuestasDia != null),
                        take(1),
                        map((respuestasDia: RespuestaDiaDTO[]) =>
                            respuestasDia.map(respuestaDia => ({
                                name: `${respuestaDia.dia.getDate()}/${respuestaDia.dia.getMonth() + 1}/${respuestaDia.dia.getFullYear()}`,
                                value: respuestaDia.cantidad
                            })))
                    )
            );
        }
        }
        if (respuestas == null) {
            return [];
        }
        const cantidadTotal =
            respuestas.reduce((cantidad, respuesta) => cantidad + (respuesta.extra?.cantidad ?? 0), 0);
        return respuestas.filter(Boolean).map(respuesta => ({
            ...respuesta,
            value: (respuesta.extra?.cantidad ?? 0) / cantidadTotal
        }));
    }

    private cargarCuotas(): Promise<Respuesta[]> {
        this.store.dispatch(new InvestigacionesAcciones.ObtenerCuotas(
            this.store.selectSnapshot((state: State) => state.investigaciones.investigacionSeleccionada)!
        ));
        return firstValueFrom(
            this.store.select((state: State) => state.investigaciones.cuotasInvestigacionSeleccionada).pipe(
                filter(cuotas => cuotas != null),
                map((cuotas: CuotaDTO[]) => cuotas.map(cuota => ({
                    name: cuota.tag.valor,
                    value: cuota.cuotaAlcanzada,
                    extra: {
                        cantidad: cuota.cuota
                    }
                } as Respuesta))),
                take(1)
            )
        );
    }

    private async cargarFunnel(): Promise<Respuesta[]> {
        return [];
    }
}
